import { Add, ArrowForward } from '@mui/icons-material';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Tab, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { setInspectionSelectedItem, setSearchValue, setSelectedItem } from 'app/actions/transport';
import { IconButton, InputSearch, Protect, Scrollbar } from 'app/components';
import { TransportRoundedIcon } from 'app/icons';
import {
  TransportDialog,
  TransportForm,
  TransportInspectionDialog,
  TransportInspectionList,
  TransportList,
  TransportMenu,
  TransportTrackForm,
} from 'app/pages/Transport';
import {
  checkedItemsSelector,
  inspectionSelectedItemSelector,
  itemsSelector,
  searchValueSelector,
  selectedItemSelector,
  sortSelectedItemSelector,
} from 'app/selectors/transport';
import { useAsyncAction } from 'app/services';
import { transportDataLoad, transportDelete, transportItemGroupUpdate, transportLoad, transportUpdate } from 'app/store/transport';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Transport = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const trackFormRef = useRef();
  const theme = useTheme();
  const items = useSelector(itemsSelector);
  const selectedItem = useSelector(selectedItemSelector);
  const checkedItems = useSelector(checkedItemsSelector);
  const searchValue = useSelector(searchValueSelector);
  const inspectionSelectedItem = useSelector(inspectionSelectedItemSelector);
  const sortSelectedItem = useSelector(sortSelectedItemSelector);

  const [tab, setTab] = useState('info');

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(transportLoad);
  const { asyncAction: fetchDetailData, isLoading: isFetchDetailLoading } = useAsyncAction(transportDataLoad);
  const [dialogInspectionOpen, setDialogInspectionOpen] = useState(false);
  const { asyncAction: updateGroupSubmit } = useAsyncAction(transportItemGroupUpdate);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(transportUpdate, [selectedItem]);
  const { asyncAction: deleteSubmit } = useAsyncAction(transportDelete, [selectedItem]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleTabChange = (event, value) => {
    setTab(value);
  };

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleFormSubmit = (params) => {
    updateSubmit(selectedItem.id, params);
  };

  const handleSearchChange = (value) => dispatch(setSearchValue(value));

  const getSearchPlaceholder = (sortColumn) => {
    switch (sortColumn) {
      case 'name':
        return 'Поиск по названию';
      case 'group_name':
        return 'Поиск по группе';
      case 'number':
        return 'Поиск по госномеру';
      case 'imei':
        return 'Поиск по IMEI';
      case 'volume':
        return 'Поиск по объему бака';
      default:
        return 'Поиск по названию';
    }
  };
  // eslint-disable-next-line no-unused-vars
  const handleDeleteClick = () => {
    deleteSubmit(selectedItem.id);
  };

  const handleGroupOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleGroupClose = () => {
    setAnchorEl(null);
  };

  const handleGroupChange = (id) => {
    if (checkedItems.length) {
      updateGroupSubmit({ ids: checkedItems.join(','), group_id: id });
    }

    setAnchorEl(null);
  };

  const handleTrackFormClick = () => {
    trackFormRef.current?.handleFormSubmit();
  };

  const handleTrackFormSubmit = (params) => {
    return params;
  };

  const handleDialogInspectionClose = () => {
    setDialogInspectionOpen(false);
  };

  useEffect(() => {
    if (!dialogInspectionOpen) {
      setTimeout(() => dispatch(setInspectionSelectedItem(null)), theme.transitions.duration.shorter);
    }
  }, [dialogInspectionOpen]);

  useEffect(() => {
    if (inspectionSelectedItem) {
      setDialogInspectionOpen(true);
    }
  }, [inspectionSelectedItem]);

  useEffect(() => {
    if (!!selectedItem && !isFetchDetailLoading) {
      fetchDetailData(selectedItem.id);
    }
  }, [selectedItem?.id]);

  useEffect(() => {
    fetchData();
    return () => dispatch(setSelectedItem(null));
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isFetchLoading && items.length !== 0 && (
          <React.Fragment>
            <Stack
              direction='row'
              alignItems='center'
              sx={(theme) => ({
                padding: theme.spacing(2),
                '.MuiButton-root.Mui-disabled': {
                  backgroundColor: 'background.default',
                  'svg path': {
                    fill: theme.palette.text.disabled,
                  },
                },
              })}>
              <InputSearch placeholder={getSearchPlaceholder(sortSelectedItem.name)} value={searchValue} onChange={handleSearchChange} />
              <Protect
                permissions={['create', 'delete', 'update']}
                renderPermitted={() => (
                  <React.Fragment>
                    <IconButton onClick={handleGroupOpen} endIcon={<ArrowForward />} disabled={!checkedItems.length} />
                    <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
                    <TransportMenu anchorEl={anchorEl} open={!!anchorEl} onClose={handleGroupClose} onChange={handleGroupChange} />
                    <TransportDialog open={dialogOpen} onClose={handleDialogClose} />
                  </React.Fragment>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            </Stack>
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={{
                flex: '1 1 auto',
                overflow: 'hidden',
              }}>
              <TransportList />
            </Stack>
          </React.Fragment>
        )}
        {!isFetchLoading && items.length === 0 && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будет отображаться транспорт
            </Typography>
          </Stack>
        )}
        {isFetchLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
          '.simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}>
        {selectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: 'auto',
                width: '100%',
                height: tab === 'inspection' ? '100%' : 'auto',
                padding: theme.spacing(2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiAvatar-circular': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    svg: {
                      width: '30px',
                      height: '30px',
                    },
                    path: {
                      fill: theme.palette.primary.main,
                    },
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <TransportRoundedIcon fontSize='large' />
                </Avatar>
                <Typography variant='body1'>{selectedItem.name}</Typography>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  height: tab === 'inspection' ? '100%' : 'auto',
                  position: 'relative',
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  '.MuiTabPanel-root': {
                    width: '100%',
                    padding: theme.spacing(2),
                  },
                })}>
                <TabContext value={tab}>
                  <TabList
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                      children: <span />,
                    }}
                    sx={(theme) => ({
                      width: '100%',
                      minHeight: '44px',
                      borderBottom: '1px solid',
                      borderColor: theme.palette.divider,
                      '.MuiTab-root': {
                        fontSize: '12px',
                        minHeight: '44px',
                        padding: theme.spacing(2),
                        '&.Mui-selected': {
                          color: 'inherit',
                        },
                      },
                      '.MuiTabs-indicator': {
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: 'transparent',
                      },
                      '.MuiTabs-indicator span': {
                        width: 'calc(100% - ' + theme.spacing(4) + ')',
                        bgcolor: theme.palette.primary.main,
                        borderRadius: theme.shape.borderRadius,
                      },
                    })}>
                    <Tab value='info' label='Информация' disableRipple />
                    <Tab value='parameters' label='Параметры СЭП' disableRipple />
                    <Tab value='inspection' label='Осмотры' disableRipple />
                  </TabList>
                  <TabPanel value='info'>
                    <Stack
                      direction='column'
                      alignItems='center'
                      sx={{
                        width: '100%',
                        position: 'relative',
                        form: {
                          width: '100%',
                        },
                      }}>
                      <TransportForm ref={formRef} defaultValues={selectedItem} onSubmit={handleFormSubmit} />
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          '.MuiAlert-root': {
                            width: '100%',
                            marginTop: theme.spacing(2),
                            borderRadius: theme.shape.borderRadius,
                          },
                        })}>
                        {!isUpdateLoading && isUpdateError && (
                          <Alert severity='error'>
                            Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                          </Alert>
                        )}
                        {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                      </Stack>
                      <Protect
                        permissions={['create', 'update', 'delete', 'read+']}
                        renderPermitted={() => (
                          <Stack
                            direction='row'
                            justifyContent='flex-end'
                            sx={(theme) => ({
                              width: '100%',
                              paddingTop: theme.spacing(3),
                            })}>
                            <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
                              Сохранить изменения
                            </LoadingButton>
                          </Stack>
                        )}
                        renderForbidden={() => <React.Fragment />}
                      />
                    </Stack>
                  </TabPanel>
                  <TabPanel value='parameters'>
                    <Stack
                      direction='column'
                      alignItems='center'
                      sx={{
                        width: '100%',
                        position: 'relative',
                        form: {
                          width: '100%',
                        },
                      }}>
                      <TransportTrackForm ref={trackFormRef} onSubmit={handleTrackFormSubmit} />
                      <Protect
                        permissions={['create', 'update', 'delete']}
                        renderPermitted={() => (
                          <Stack
                            direction='row'
                            justifyContent='flex-end'
                            sx={(theme) => ({
                              width: '100%',
                              paddingTop: theme.spacing(3),
                            })}>
                            <LoadingButton variant='contained' onClick={handleTrackFormClick} disableElevation>
                              Сформировать файл и отправить
                            </LoadingButton>
                          </Stack>
                        )}
                        renderForbidden={() => <React.Fragment />}
                      />
                    </Stack>
                  </TabPanel>
                  <TabPanel value='inspection' style={{ height: '100%', padding: '0' }}>
                    {!isFetchDetailLoading && selectedItem.inspections.length !== 0 && (
                      <Stack
                        direction='row'
                        flexDirection='column'
                        sx={{
                          flex: '1 1 auto',
                          height: '100%',
                        }}>
                        <TransportInspectionList />
                        <TransportInspectionDialog
                          open={dialogInspectionOpen}
                          onClose={handleDialogInspectionClose}
                          defaultValues={inspectionSelectedItem}
                        />
                      </Stack>
                    )}
                    {!isFetchDetailLoading && selectedItem.inspections.length === 0 && (
                      <Stack
                        direction='column'
                        alignItems='center'
                        justifyContent='center'
                        sx={(theme) => ({
                          width: '100%',
                          height: '100%',
                          padding: theme.spacing(2),
                        })}>
                        <Typography align='center' variant='body1' component='div'>
                          Здесь пока ничего нет
                        </Typography>
                        <Typography align='center' color='text.secondary' variant='caption' component='div'>
                          В этом списке будут отображаться осмотры транспортного средства.
                        </Typography>
                      </Stack>
                    )}
                    {isFetchDetailLoading && (
                      <Stack
                        direction='column'
                        alignItems='center'
                        justifyContent='center'
                        sx={(theme) => ({
                          width: '100%',
                          height: '100%',
                          padding: theme.spacing(2),
                        })}>
                        <CircularProgress size={24} />
                      </Stack>
                    )}
                  </TabPanel>
                </TabContext>
              </Stack>
            </Stack>
          </Scrollbar>
        )}
        {!selectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите транспорт из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default Transport;
